// See docs/development/endpoints/db-branch-config.md for the full design/rationale behind this
// module — comments below are kept to short invariants, not the "why".
export type DbBranchUiType = 'mysql' | 'postgres' | 'mongo' | 'redis';

/** mirrord's actual accepted enum values for db_branches[].type (e.g. "postgres" → "pg"). Single
 *  source of truth for client and server. */
export const DB_TYPE_TO_MIRRORD: Record<DbBranchUiType, string> = {
  mysql:    'mysql',
  postgres: 'pg',
  mongo:    'mongodb',
  redis:    'redis',
};

// ---------------------------------------------------------------------------
// Per-DB-type field definitions
// ---------------------------------------------------------------------------
interface DbField { key: string; label: string; placeholder: string; optional?: boolean }

export const DB_FIELDS: Record<DbBranchUiType, DbField[]> = {
  mysql: [
    { key: 'host',     label: 'Host env var',     placeholder: 'e.g. MYSQL_HOST' },
    { key: 'port',     label: 'Port env var',     placeholder: 'e.g. MYSQL_PORT',     optional: true },
    { key: 'user',     label: 'User env var',     placeholder: 'e.g. MYSQL_USERNAME' },
    { key: 'password', label: 'Password env var', placeholder: 'e.g. MYSQL_PASSWORD', optional: true },
    { key: 'database', label: 'Database env var', placeholder: 'e.g. MYSQL_DATABASE' },
  ],
  postgres: [
    { key: 'host',     label: 'Host env var',     placeholder: 'e.g. POSTGRES_HOST' },
    { key: 'port',     label: 'Port env var',     placeholder: 'e.g. POSTGRES_PORT',     optional: true },
    { key: 'user',     label: 'User env var',     placeholder: 'e.g. POSTGRES_USER' },
    { key: 'password', label: 'Password env var', placeholder: 'e.g. POSTGRES_PASSWORD', optional: true },
    { key: 'database', label: 'Database env var', placeholder: 'e.g. POSTGRES_DB' },
  ],
  mongo: [
    { key: 'host',     label: 'Host env var',     placeholder: 'e.g. MONGO_HOST' },
    { key: 'port',     label: 'Port env var',     placeholder: 'e.g. MONGO_PORT',     optional: true },
    { key: 'user',     label: 'User env var',     placeholder: 'e.g. MONGO_USER',     optional: true },
    { key: 'password', label: 'Password env var', placeholder: 'e.g. MONGO_PASSWORD', optional: true },
    { key: 'database', label: 'Database env var', placeholder: 'e.g. MONGO_DATABASE' },
  ],
  redis: [
    { key: 'host',     label: 'Host env var',     placeholder: 'e.g. REDIS_HOST' },
    { key: 'port',     label: 'Port env var',     placeholder: 'e.g. REDIS_PORT',     optional: true },
    { key: 'password', label: 'Password env var', placeholder: 'e.g. REDIS_PASSWD or REDIS_PASSWORD' },
  ],
};

export const DB_DEFAULT_VERSIONS: Partial<Record<DbBranchUiType, string>> = {
  mysql:    '5.7',
  postgres: '15',
  mongo:    '6.0',
  // redis: no version field in db_branches
};

// All patterns use (?:^|_)KEYWORD(?:_|$) so they match only whole underscore-delimited
// segments — avoids false positives like PASSENGER→password, USERINFO→user, IDP_NAME→database.
export const FIELD_PATTERNS: Record<string, RegExp> = {
  host:     /(?:^|_)(?:HOST|ADDR)(?:_|$)|ENDPOINT/i,
  port:     /(?:^|_)PORT(?:_|$)/i,
  user:     /(?:^|_)(?:USER|USERNAME)(?:_|$)/i,
  password: /(?:^|_)(?:PASSWORD|PASSWD|PWD|PASS)(?:_|$)/i,
  // `DB` only matches as the final segment (e.g. MYSQL_DB) — a bare mid-name `DB_` would false-
  // match DB_MIN_POOL_SIZE. `SCHEMA` excludes SCHEMA_REGISTRY (Confluent Kafka noise).
  database: /(?:^|_)DATABASE(?:_|$)|(?:^|_)(?:DB_NAME|DBNAME)(?:_|$)|(?:^|_)SCHEMA(?!_REGISTRY)(?:_|$)|(?:^|_)DB$/i,
};

// Same as FIELD_PATTERNS.database minus the bare `(?:^|_)DB$` alternative — that alternative is
// too weak to trust with no other signal backing it up (e.g. Keycloak's `KC_DB`, its DB-vendor
// selector, not a schema name). Only used by the no-signal fallback tier below.
export const DATABASE_PATTERN_SANS_BARE_DB =
  /(?:^|_)DATABASE(?:_|$)|(?:^|_)(?:DB_NAME|DBNAME)(?:_|$)|(?:^|_)SCHEMA(?!_REGISTRY)(?:_|$)/i;

// Prefer env vars that also match the DB type keyword (e.g. POSTGRES_HOST over MYSQL_HOST).
export const DB_TYPE_KEYWORDS: Record<DbBranchUiType, RegExp> = {
  mysql:    /MYSQL/i,
  postgres: /POSTGRES|POSTGRESQL|_PG_|^PG_/i,
  mongo:    /MONGO/i,
  redis:    /REDIS/i,
};

// Maps infra dependency name from appmetadata to DbBranchUiType. Matches the keyword as a whole
// hyphen/underscore-delimited segment, not just an exact name — real appmetadata dependency names
// are frequently compound/prefixed/versioned (e.g. "mysql-mystr", "member-master-mysql",
// "cqs-command-center-postgresql", "dts-sls-redis", "mongodb-7.0.0"), not just bare "mysql"/"redis".
// Deliberately does NOT match unrelated same-prefix products like "redisstackserver".
const DB_DEP_REGEX: Record<DbBranchUiType, RegExp> = {
  mysql:    /(?:^|[-_])mysql(?:[-_]|$)/i,
  postgres: /(?:^|[-_])(?:postgres(?:ql)?|pg)(?:[-_]|$)/i,
  mongo:    /(?:^|[-_])mongo(?:db)?(?:[-_]|$)/i,
  redis:    /(?:^|[-_])redis(?:[-_]|$)/i,
};

export function inferDbType(depName: string): DbBranchUiType | null {
  for (const [type, re] of Object.entries(DB_DEP_REGEX) as [DbBranchUiType, RegExp][]) {
    if (re.test(depName)) return type;
  }
  return null;
}

// The exact, full tag as it literally appears on the image — e.g. "9.6.3", never truncated.
// Use this (not inferVersionFromImage) to resolve a template's `$RANCH_INFERRED_VERSION` token in
// an `image` field: that string gets spliced directly into a real Artifactory image reference, so
// it must be a tag that actually exists there. inferVersionFromImage's major/major.minor-only
// output is correct for the separate bare `version` field (the operator maps that short form to
// its own known-good image internally) but produces a non-existent tag here — e.g. "9.6" from
// "9.6.3" 404s, since Artifactory only publishes the full patch version.
export function extractFullImageTag(image: string): string | undefined {
  const noDigest = image.split('@')[0];
  const lastSegment = noDigest.split('/').pop() ?? '';
  return lastSegment.includes(':') ? lastSegment.split(':').pop() || undefined : undefined;
}

export function inferVersionFromImage(image: string, dbType?: DbBranchUiType): string | undefined {
  // Strip any digest first (`postgres@sha256:...`) so its hex portion isn't misread as a version tag.
  const noDigest = image.split('@')[0];
  const lastSegment = noDigest.split('/').pop() ?? '';
  const tag = lastSegment.includes(':') ? lastSegment.split(':').pop() ?? '' : '';
  if (dbType === 'redis') return tag || undefined;
  // For mysql/postgres/mongo, `version` picks a base image the operator maps internally (see
  // db-branch-config.md) rather than being forwarded as a free-form semver string. DB_DEFAULT_VERSIONS
  // below (mysql "5.7", postgres "15", mongo "6.0") shows the shape the operator actually expects:
  // major-only for mysql/postgres unless a non-zero minor is present, and always major.minor for
  // mongo. A three-segment tag like "15.3.1" or a trailing-zero minor like "8.0" is not known to be
  // an accepted value, so keep producing only major or major.minor here (never a patch segment) to
  // match the shipped behavior this was silently deviating from.
  const tagVersion = tag.match(/^(\d+)(?:\.(\d+))?/);
  if (tagVersion && tagVersion[1] !== '0') {
    const [, major, minor] = tagVersion;
    return minor && (dbType === 'mongo' || minor !== '0') ? `${major}.${minor}` : major;
  }
  const imageName = lastSegment.split(':')[0];
  return imageName.match(/[^a-z](\d+)(?:\.|$)/i)?.[1];
}

// Count how many DB field patterns a var name matches — used to rank fallback-pool candidates.
const dbSpecificityScore = (name: string) =>
  Object.values(FIELD_PATTERNS).filter(p => p.test(name)).length;

// A composite connection-string var (e.g. a full JDBC URL) can coincidentally match a single
// field's pattern by name (e.g. one literally called DATABASE_URL) while being the wrong value
// entirely — deprioritize these below any plain single-value candidate.
const isCompositeUrlVar = (name: string) => /(?:^|_)URL(?:_|$)/i.test(name);

// K8s injects {SVC}_PORT_{n}_TCP_ADDR etc. — matches field patterns but is meaningless for DB config.
const K8S_TCP_VAR = /_PORT_\d+_/i;
// K8s also injects {SVC}_SERVICE_HOST / {SVC}_SERVICE_PORT(_NAME) for every Service object in the
// namespace — present on every pod regardless of whether the app itself reads them.
const K8S_SERVICE_VAR = /_SERVICE_(?:HOST|PORT)(?:_|$)/i;
// Bare K8s service port: exactly ONE_WORD_PORT — keep when the prefix matches the target DB type,
// or when a same-prefix bare {PREFIX}_HOST/_ADDR exists (k8s never auto-injects a bare host var,
// so its presence proves this workload deliberately set the pair itself). Tests the full name (not
// just the prefix) so anchored keywords like postgres's `^PG_` still match.
const isK8sSvcPort = (name: string, typeKeyword: RegExp, barePlainHostPrefixes: Set<string>) => {
  const m = name.match(/^([A-Z][A-Z0-9]*)_PORT$/i);
  return m !== null && !typeKeyword.test(name) && !barePlainHostPrefixes.has(m[1].toUpperCase());
};

// Normalizes an appmetadata dependency name (e.g. "member-master-mysql") into the form it'd
// take as an env var prefix ("MEMBER_MASTER_MYSQL"), so it can be matched as a substring.
const normalizeDepName = (depName: string) => depName.toUpperCase().replace(/[^A-Z0-9]+/g, '_');

// DB-engine words carry no identifying signal on their own (every same-type dependency shares
// them) — stripped from the token set before comparing. See db-branch-config.md for why.
const DB_ENGINE_WORDS = new Set(['MYSQL', 'POSTGRESQL', 'POSTGRES', 'PG', 'REDIS', 'MONGO', 'MONGODB']);
const tokenize = (s: string) => s.toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);

// Does this var name look like it belongs to the specific dependency (e.g. "MEMBER_MASTER_MYSQL_PORT"
// for dep "member-master-mysql"), as opposed to some other same-type instance sharing the namespace
// (e.g. "MYSQL_CCM_PORT", "MYSQL_ERX_PORT")? Token-set comparison (order-independent), with the
// dependency's leading token optionally dropped — see db-branch-config.md. Deliberately returns
// false (not true) when no distinguishing token remains after stripping engine words and bare
// numeric tokens (e.g. "16" in "postgresql-16") — a wrong credential is worse than a missing field.
const hasDepAffinity = (name: string, normalizedDepName: string) => {
  if (!normalizedDepName) return false;
  const depTokens = tokenize(normalizedDepName).filter(t => !DB_ENGINE_WORDS.has(t) && !/^\d+$/.test(t));
  if (depTokens.length === 0) return false;
  const nameTokens = new Set(tokenize(name));
  if (depTokens.every(t => nameTokens.has(t))) return true;
  const withoutLeading = depTokens.slice(1);
  return withoutLeading.length > 0 && withoutLeading.every(t => nameTokens.has(t));
};

const isK8sDiscoveryPortVar = (name: string, typeKeyword: RegExp, barePlainHostPrefixes: Set<string>) => {
  const m = name.match(/^([A-Z][A-Z0-9_]*)_PORT$/i);
  if (m === null || !typeKeyword.test(name)) return false;
  const hasExtraToken = tokenize(m[1]).filter(t => !DB_ENGINE_WORDS.has(t)).length > 0;
  return hasExtraToken || !barePlainHostPrefixes.has(m[1].toUpperCase());
};

// Resolves the `db_branches[].name` (schema name) a toggle/auto-enable path should use: an
// explicit appmetadata override wins, then a live env-var-value match (keyed by the resolved
// `database` param's own env var name, or the sole DATABASE-pattern-matching live var if that
// didn't resolve one), else ''. Shared by ServiceOverridesPanel's manual toggle and
// OverlayCandidatesStep's auto-enable effect so the two paths can't drift out of sync again —
// see db-branch-config.md.
export function resolveDbBranchName(
  // Same shape as DbBranchParamValue (defined in apiHelpers.ts) — inlined here to avoid a circular
  // import, since apiHelpers.ts already imports from this module.
  params: Record<string, string | string[] | { env_var_name: string; value_pattern?: string }>,
  dbSchemaNames: Record<string, string>,
  dbType: DbBranchUiType,
  schemaNameOverride?: string,
): string {
  if (schemaNameOverride) return schemaNameOverride;
  // Redis `name` is a numeric DB index, not a schema — never infer it.
  if (dbType === 'redis') return '';
  const databaseEnvVar = params.database;
  // A candidate-list `database` value has no single env var to resolve a live schema name
  // from — the operator itself picks one at apply time, which Ranch can't predict here.
  const databaseEnvVarName = typeof databaseEnvVar === 'string'
    ? databaseEnvVar
    : Array.isArray(databaseEnvVar) ? undefined : databaseEnvVar?.env_var_name;
  const dbSchemaNameEntries = Object.entries(dbSchemaNames).filter(([key]) => DATABASE_PATTERN_SANS_BARE_DB.test(key));
  return (databaseEnvVarName ? dbSchemaNames[databaseEnvVarName] : undefined)
    ?? (dbSchemaNameEntries.length === 1 ? dbSchemaNameEntries[0][1] : undefined)
    ?? '';
}

export function matchEnvVarsToDbFields(names: string[], dbType: DbBranchUiType, depName?: string): Record<string, string> {
  const typeKeyword = DB_TYPE_KEYWORDS[dbType];
  const otherTypeKeywords = (Object.entries(DB_TYPE_KEYWORDS) as [DbBranchUiType, RegExp][])
    .filter(([t]) => t !== dbType)
    .map(([, re]) => re);
  const normalizedDepName = depName ? normalizeDepName(depName) : '';

  const isBareEngineDep = normalizedDepName.length > 0
    && tokenize(normalizedDepName).filter(t => !DB_ENGINE_WORDS.has(t)).length === 0;
  const isGenericK8sServiceVar = (n: string) =>
    K8S_SERVICE_VAR.test(n) && !hasDepAffinity(n, normalizedDepName) && !(isBareEngineDep && typeKeyword.test(n));

  // No typeKeyword gate — k8s never auto-injects a bare {PREFIX}_HOST/_ADDR, so its presence
  // proves this workload deliberately set the pair, regardless of what {PREFIX} is.
  const barePlainHostPrefixes = new Set(
    names
      .map(n => n.match(/^([A-Z][A-Z0-9]*)_(?:HOST|ADDR)$/i))
      .filter((m): m is RegExpMatchArray => m !== null)
      .map(m => m[1].toUpperCase()),
  );

  // k8s guarantees {PREFIX}_SERVICE_PORT is always a bare port number; a same-prefix legacy
  // Docker-links {PREFIX}_PORT (no "_SERVICE_") is always a full "tcp://ip:port" URI. When both
  // exist, the bare one must win regardless of name length — confirmed live 2026-08-11:
  // CQS_COMMAND_CENTER_POSTGRESQL_PORT="tcp://192.168.120.164:5432" was winning over the correct,
  // bare CQS_COMMAND_CENTER_POSTGRESQL_SERVICE_PORT="5432" purely for being the shorter name.
  const namesSet = new Set(names.map(n => n.toUpperCase()));
  const isLegacyPortWithBareSibling = (name: string) => {
    const m = name.match(/^([A-Z][A-Z0-9_]*)_PORT$/i);
    return m !== null && namesSet.has(`${m[1].toUpperCase()}_SERVICE_PORT`);
  };

  const pool = names
    .filter(n => !K8S_TCP_VAR.test(n))
    .filter(n => !isGenericK8sServiceVar(n))
    .filter(n => !isK8sSvcPort(n, typeKeyword, barePlainHostPrefixes))
    .filter(n => !otherTypeKeywords.some(re => re.test(n)))
    .sort((a, b) => {
      const aType = typeKeyword.test(a) ? 1 : 0;
      const bType = typeKeyword.test(b) ? 1 : 0;
      if (bType !== aType) return bType - aType;

      // A legacy {PREFIX}_PORT with a bare {PREFIX}_SERVICE_PORT sibling is guaranteed to hold a
      // URI, not a number — rank it below its sibling before any other tie-break gets a say.
      const aLegacyPort = isLegacyPortWithBareSibling(a) ? 1 : 0;
      const bLegacyPort = isLegacyPortWithBareSibling(b) ? 1 : 0;
      if (aLegacyPort !== bLegacyPort) return aLegacyPort - bLegacyPort;

      const aSvc = (K8S_SERVICE_VAR.test(a) || isK8sDiscoveryPortVar(a, typeKeyword, barePlainHostPrefixes)) ? 1 : 0;
      const bSvc = (K8S_SERVICE_VAR.test(b) || isK8sDiscoveryPortVar(b, typeKeyword, barePlainHostPrefixes)) ? 1 : 0;
      if (aSvc !== bSvc) return aSvc - bSvc;
      // Prefer vars that specifically reference this dependency's own name over lookalike
      // vars belonging to other same-type instances sharing the namespace.
      const aDep = hasDepAffinity(a, normalizedDepName) ? 1 : 0;
      const bDep = hasDepAffinity(b, normalizedDepName) ? 1 : 0;
      if (bDep !== aDep) return bDep - aDep;
      // Composite connection-string vars (JDBC URLs, etc.) rank last — never the right value
      // for a single field, even when their name happens to also match that field's pattern.
      const aUrl = isCompositeUrlVar(a) ? 1 : 0;
      const bUrl = isCompositeUrlVar(b) ? 1 : 0;
      if (aUrl !== bUrl) return aUrl - bUrl;
      return dbSpecificityScore(b) - dbSpecificityScore(a) || a.length - b.length;
    });

  const result: Record<string, string> = {};
  const used = new Set<string>();
  for (const field of DB_FIELDS[dbType]?.map(f => f.key) ?? []) {
    const pattern = FIELD_PATTERNS[field];
    if (!pattern) continue;

    const isK8sNoiseVar = (n: string) =>
      K8S_SERVICE_VAR.test(n) || isK8sSvcPort(n, typeKeyword, barePlainHostPrefixes) || isK8sDiscoveryPortVar(n, typeKeyword, barePlainHostPrefixes);

    let idx = pool.findIndex(n => !used.has(n) && pattern.test(n) && typeKeyword.test(n) && !isK8sNoiseVar(n));
    if (idx === -1) idx = pool.findIndex(n => !used.has(n) && pattern.test(n) && hasDepAffinity(n, normalizedDepName));
    // Last resort before k8s-noise fallback: any remaining plain, non-noise var, even with no
    // distinguishing signal — but only if it's the sole such candidate (a wrong credential is
    // worse than a missing field). Only for host/port/database; user/password never guess blind.
    if (idx === -1 && (field === 'host' || field === 'port' || field === 'database')) {
      const fallbackPattern = field === 'database' ? DATABASE_PATTERN_SANS_BARE_DB : pattern;
      const candidates = pool.filter(n => !used.has(n) && fallbackPattern.test(n) && !isK8sNoiseVar(n));
      if (candidates.length === 1) {
        idx = pool.indexOf(candidates[0]);
      } else if (candidates.length > 1) {
        // Break ties in favor of a candidate also containing the generic "DB" token, if that
        // narrows it back to exactly one (e.g. ranch's DB_HOST among several unrelated *_ENDPOINT vars).
        const dbTagged = candidates.filter(n => tokenize(n).includes('DB'));
        if (dbTagged.length === 1) idx = pool.indexOf(dbTagged[0]);
      }
    }
    // Last resort: a type-matching k8s-noise var — still useful when it's genuinely the only
    // signal available (e.g. a bare dependency name with no plain var at all).
    if (idx === -1) idx = pool.findIndex(n => !used.has(n) && pattern.test(n) && typeKeyword.test(n));
    if (idx === -1 && !normalizedDepName && field !== 'user' && field !== 'password') {
      idx = pool.findIndex(n => !used.has(n) && pattern.test(n));
    }
    if (idx !== -1) {
      result[field] = pool[idx];
      used.add(pool[idx]);
    }
  }
  return result;
}
